import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { MultiSelect } from '../components/ui/multi-select';
import { BulkEntryGrid } from '../components/BulkEntryGrid';
import { useCompany } from '../contexts/CompanyContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Layers } from 'lucide-react';

const BulkFinishQualities = () => {
  const navigate = useNavigate();
  const { activeCompany } = useCompany();
  const [qualities, setQualities] = useState([]);
  const [selectedQualityIds, setSelectedQualityIds] = useState([]);

  useEffect(() => {
    const fetchQualities = async () => {
      try {
        const res = await api.get('/qualities');
        setQualities(res.data);
      } catch {
        toast.error('Failed to load qualities');
      }
    };
    fetchQualities();
  }, [activeCompany?.company_id]);

  const qualityOptions = useMemo(
    () => qualities.map(q => ({ value: q.quality_id, label: `${q.quality_name} (${q.default_unit})` })),
    [qualities]
  );

  const selectedQualities = useMemo(
    () => qualities.filter(q => selectedQualityIds.includes(q.quality_id)),
    [qualities, selectedQualityIds]
  );

  const columns = useMemo(() => [
    {
      key: 'subquality_name', label: 'Finish Quality Name', required: true, width: '280px',
      placeholder: 'e.g. Bleached, Dyed Red',
    },
    {
      key: 'description', label: 'Description', width: '320px',
      placeholder: 'Optional notes',
    },
  ], []);

  const handleSave = async (rows) => {
    if (!selectedQualityIds || selectedQualityIds.length === 0) {
      toast.error('Please select at least one parent quality first');
      return { success_count: 0, error_count: rows.length, errors: [{ row: 0, detail: 'No parent qualities selected' }] };
    }
    const res = await api.post('/bulk/subqualities', { quality_ids: selectedQualityIds, rows });
    if (res.data.success_count > 0) {
      toast.success(`${res.data.success_count} finish quality/qualities created successfully`);
    }
    if (res.data.error_count > 0) {
      toast.error(`${res.data.error_count} row(s) had errors`);
    }
    return res.data;
  };

  return (
    <div className="space-y-6" data-testid="bulk-finish-qualities-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="bulk-finish-qualities-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Bulk Add Finish Qualities</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Add multiple finish qualities; each row will be linked to all selected parent qualities · <span className="theme-text font-medium">{activeCompany?.name}</span>
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/masters')} className="gap-1.5">
          <Layers className="h-4 w-4" /> View Masters
        </Button>
      </div>

      {/* Parent Qualities Multi-Selector */}
      <Card className="border-l-4" style={{ borderLeftColor: 'var(--c-500)' }}>
        <CardContent className="p-4">
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Select Parent Qualities * <span className="text-xs text-slate-500 font-normal">(one or more)</span></Label>
          <div className="flex flex-col gap-3">
            <MultiSelect
              value={selectedQualityIds}
              onChange={setSelectedQualityIds}
              options={qualityOptions}
              placeholder="Choose one or more qualities..."
              testId="bulk-fq-parent-multiselect"
              className="max-w-xl"
            />
            {selectedQualities.length > 0 && (
              <span className="text-xs text-slate-500">
                {selectedQualities.length} parent {selectedQualities.length === 1 ? 'quality' : 'qualities'} selected — each finish quality below will be linked to all of them.
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {selectedQualityIds.length > 0 ? (
        <Card>
          <CardContent className="p-6">
            <BulkEntryGrid
              columns={columns}
              onSave={handleSave}
              entityName="Finish Quality"
              testIdPrefix="bulk-fq"
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <Layers className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Select one or more parent qualities above to start adding finish qualities</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkFinishQualities;
